import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultLayout from '../../layout/DefaultLayout';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import creditoService from '../../services/creditoService';
import { useGlobalState } from '../../state';
import { toCurrency } from '../../utils/formaters';
import { FaPhone, FaHandshake, FaCircle } from 'react-icons/fa';
import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';

type CreditoDto = {
  id: string | number;
  lineaDeCredito?: { nombre?: string };
  monto?: number;
  fechaEntradaEnVigencia?: string;
  estadoSolicitud?: { descripcion?: string; id?: number };
};

const Creditos: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useGlobalState('user');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [creditos, setCreditos] = useState<CreditoDto[] | undefined>(undefined);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(undefined);

      // Wait for auth check
      if (user === undefined) {
        // still checking
        return;
      }
      if (!user) {
        navigate('/auth/signin');
        return;
      }

      try {
          const result: any = await creditoService.getCreditosParaUsuario?.();
        // result shape: BaseResponse { success, data, errors }
        if (result?.success) {
          setCreditos(result.data ?? []);
        } else {
          setError(result?.errors?.[0] ?? 'Ocurrió un error al cargar los créditos');
        }
      } catch (ex: any) {
        setError(ex?.message ?? 'Ocurrió un error al cargar los créditos');
      } finally {
        setLoading(false);
      }
    };

    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const statusColor = (estadoId?: number) => {
    if (estadoId === 4) return 'text-green-600 bg-green-50 border-green-200';
    if (estadoId === 3) return 'text-red-600 bg-red-50 border-red-200';
    return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  };

  const PrestamoItem: React.FC<{ item: CreditoDto; onClick: () => void }> = ({ item, onClick }) => {
    const nombre = item.lineaDeCredito?.nombre ?? '—';
    const monto = item.monto ?? 0;
    const fecha = item.fechaEntradaEnVigencia ? new Date(item.fechaEntradaEnVigencia).toLocaleDateString() : '';
    const estado = item.estadoSolicitud?.descripcion ?? '';
    const estadoId = item.estadoSolicitud?.id;

    return (
      <button
        onClick={onClick}
        className="w-full text-left bg-white dark:bg-boxdark rounded-lg p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
        aria-label={`Ver detalle crédito ${nombre}`}
      >
        <div className={`flex items-center justify-center rounded-full h-12 w-12 border ${estadoId === 4 ? 'border-green-400' : estadoId === 3 ? 'border-red-400' : 'border-yellow-400'} ${estadoId === 4 ? 'bg-green-50' : estadoId === 3 ? 'bg-red-50' : 'bg-yellow-50'}`}>
          <FaHandshake className="text-gray-700" />
        </div>

        <div className="flex-1">
          <div className="text-title-sm font-semibold text-gray-800 dark:text-white">{nombre}</div>
          <div className="text-xs text-gray-500 mt-1">{estado}</div>
        </div>

        <div className="text-right w-40">
          <div className="text-title-sm font-bold text-gray-800 dark:text-white">{toCurrency(monto)}</div>
          <div className="text-xs text-gray-500 mt-1">{fecha}</div>
        </div>
      </button>
    );
  };

  const goToDetalle = (id: string | number) => {
      navigate(`/prestamos/detalle-credito/${id}`);
  };

  const goToSimular = () => {
    navigate('/prestamos/simular');
  };

  return (
      <DefaultLayout>

      <Breadcrumb pageName="Préstamos" />

      <div className="p-2">
              <div className="md:max-w-6xl mx-auto">
                  <h1 className="text-2xl font-semibold mt-12">Solicitar un préstamo</h1>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 mt-6 gap-4 bg-white dark:bg-boxdark rounded-xl p-6">           
                    <p className="text-title-xsm text-gray-500 mt-6">Hacé una simulación rápida de tu futuro préstamo</p>
                    <div className="ml-auto md:ml-0">
                      <button
                        onClick={goToSimular}
                        className="inline-flex items-center gap-2 bg-primario-celeste-800 text-white px-4 py-2 rounded font-semibold hover:bg-primario-azul-900"
                      >
                        Simular
                      </button>
                    </div>
              </div>

          {error && <Alert type="failure" message={error} />}

          {loading ? (
            <div className="rounded bg-white p-6">
              <Loading statusText="Cargando" />
            </div>
          ) : (
            <div className="space-y-4">

               <h1 className="text-2xl font-semibold mt-12">Historial de préstamos</h1>
              {creditos && creditos.length > 0 ? (
                <div className="grid gap-4">
                  {creditos.map((c: any) => (
                    <PrestamoItem key={String(c.id)} item={c} onClick={() => goToDetalle(c.id)} />
                  ))}
                </div>
              ) : (
                <div className="bg-white dark:bg-boxdark rounded p-6 text-center text-gray-600">
                  Aún no has solicitado ningún préstamo.
                </div>
              )}
            </div>
          )}
              </div>

        <div className="flex gap-3 justify-center mt-8">
            <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded"
            >
                Volver
            </button>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default Creditos;